import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';

export const Icon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

interface WrapperProps {
  $size?: 'large' | 'medium' | 'small' | 'tiny';
  $tone?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  $variant?: 'square';
}

export const Wrapper = styled.div<WrapperProps>`
  --avatar-default-size: ${size(7)};
  --avatar-default-border-radius: ${size(3)};
  --avatar-default-font-size: ${(props) => props.theme.font.size.large};
  --avatar-default-icon-size: ${(props) => props.theme.font.size.xxlarge};

  position: relative;
  width: var(--avatar-size, var(--avatar-default-size));
  height: var(--avatar-size, var(--avatar-default-size));
  background-color: var(--avatar-bg);
  color: var(--avatar-fg);
  border-radius: var(--avatar-border-radius, var(--avatar-default-border-radius));
  font-size: var(--avatar-font-size, var(--avatar-default-font-size));

  ${Icon} {
    font-size: var(--avatar-icon-size, var(--avatar-default-icon-size));
  }

  ${({ $size }) =>
    $size === 'tiny'
      ? css`
          --avatar-size: ${size(2)};
          --avatar-font-size: ${(props) => props.theme.font.size.xxxsmall};
          --avatar-icon-size: ${(props) => props.theme.font.size.xxsmall};
          --avatar-border-radius: ${size(0.75)};
        `
      : $size === 'small'
        ? css`
            --avatar-size: ${size(3)};
            --avatar-font-size: ${(props) => props.theme.font.size.xsmall};
            --avatar-icon-size: ${(props) => props.theme.font.size.small};
            --avatar-border-radius: ${size(1)};
          `
        : $size === 'medium'
          ? css`
              --avatar-size: ${size(5)};
              --avatar-font-size: ${(props) => props.theme.font.size.regular};
              --avatar-icon-size: ${size(3)};
              --avatar-border-radius: ${size(2)};
            `
          : $size === 'large'
            ? css`
                --avatar-size: ${size(8)};
                --avatar-font-size: ${(props) => props.theme.font.size.large};
                --avatar-icon-size: ${size(3)};
                --avatar-border-radius: ${size(3)};
              `
            : undefined}

  ${({ $tone }) =>
    $tone === 'primary'
      ? css`
          --avatar-bg: var(--c-primary-bg);
          --avatar-fg: var(--c-primary-fg);
        `
      : $tone === 'secondary'
        ? css`
            --avatar-bg: var(--c-secondary-bg);
            --avatar-fg: var(--c-secondary-fg);
          `
        : $tone === 'success'
          ? css`
              --avatar-bg: var(--c-semantic-success-300);
              --avatar-fg: var(--c-semantic-success-700);
            `
          : $tone === 'warning'
            ? css`
                --avatar-bg: var(--c-semantic-warning-300);
                --avatar-fg: var(--c-semantic-warning-700);
              `
            : $tone === 'error'
              ? css`
                  --avatar-bg: var(--c-semantic-error-300);
                  --avatar-fg: var(--c-semantic-error-700);
                `
              : $tone === 'info'
                ? css`
                    --avatar-bg: var(--c-semantic-info-300);
                    --avatar-fg: var(--c-semantic-info-700);
                  `
                : $tone === 'neutral'
                  ? // av alexandra
                    css`
                      --avatar-bg: var(--c-semantic-neutral-100);
                      --avatar-fg: var(--c-semantic-neutral-700);
                    `
                  : css`
                      --avatar-bg: var(--c-accent-bg);
                      --avatar-fg: var(--c-accent-fg);
                    `}

  ${({ $variant }) =>
    $variant === 'square'
      ? css`
          border-radius: ${size(1)};
        `
      : undefined}
`;

interface ImageProps {
  $contain?: boolean;
  $logotype?: boolean;
  $size?: 'tiny' | 'small' | 'medium' | 'large';
}

export const Image = styled.img<ImageProps>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;

  ${({ $contain }) =>
    $contain &&
    css`
      object-fit: contain;
    `}

  ${({ $logotype, $size }) =>
    $logotype &&
    css`
      object-fit: contain;
      padding: ${$size === 'tiny' ? size(0.1) : size(0.5)};
    `}
`;

export const Initials = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  font-weight: var(--font-weight-bold);
  text-align: center;
  text-transform: uppercase;
  user-select: none;
`;

export const Notification = styled.div`
  position: absolute;
  top: 0;
  right: ${size(-1)};
  display: block;

  > * {
    outline: 2px solid var(--c-bg-main);
  }
`;
